import React, { useState } from 'react';

// Safe text renderer that handles bold text and line breaks without XSS risk
const SafeText = ({ text }) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part.split('\n').map((line, lineIndex, arr) => (
          <React.Fragment key={`${index}-${lineIndex}`}>
            {line}
            {lineIndex < arr.length - 1 && <br />}
          </React.Fragment>
        ));
      })}
    </>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      id: "faq-1",
      question: "How do I book a session?",
      answer: "Visit the Bookings page and fill out the enquiry form with your details. I'll respond within 24-48 hours to confirm availability and discuss your preferences."
    },
    {
      id: "faq-2",
      question: "What information do I need to provide?",
      answer: "Please include your full name, contact details, preferred date/time, duration, and any specific requests. Screening is required for all new clients to ensure mutual comfort and safety."
    },
    {
      id: "faq-3",
      question: "What is your screening process?",
      answer: "For your safety and mine, I require verification for all bookings. This may include ID check or references from other companions. All information is kept strictly confidential."
    },
    {
      id: "faq-4",
      question: "Where do sessions take place?",
      answer: "I offer both incall (my private location in Sydney) and outcall (your hotel or residence) services. Outcalls require advance notice and may include a travel fee depending on location."
    },
    {
      id: "faq-5",
      question: "What should I expect during our time together?",
      answer: "Expect genuine connection, effortless conversation, and an experience tailored to your desires. Whether you're seeking a Sweet or Naughty GFE, I create a space where you can fully relax and be yourself."
    },
    {
      id: "faq-6",
      question: "What are your rates and minimum booking time?",
      answer: "My rates vary by experience type and duration. Please visit the Experience page for detailed pricing. My minimum booking is 1 hour for first-time clients, with longer arrangements available and encouraged."
    },
    {
      id: "faq-7",
      question: "Do you offer overnight or travel companionship?",
      answer: "Absolutely! Extended bookings and travel companionship are among my favorite experiences. Whether it's a dinner date that turns into an overnight stay or a weekend getaway, I'm available for longer arrangements. Contact me to discuss details."
    },
    {
      id: "faq-8",
      question: "How far in advance should I book?",
      answer: "I recommend booking at least 48 hours in advance to ensure availability. For extended dates or travel companionship, please reach out at least one week ahead. Last-minute bookings may be accommodated when possible."
    },
    {
      id: "faq-9",
      question: "What about discretion and privacy?",
      answer: "Your privacy is paramount. All communications and personal information are kept strictly confidential. I maintain the highest level of discretion in all interactions and expect the same respect in return."
    },
    {
      id: "faq-10",
      question: "Can I bring a gift?",
      answer: "Gifts are never expected but always appreciated! If you'd like to surprise me, my wishlist can be found on my Throne page. Thoughtful gestures like flowers, champagne, or lingerie are also lovely."
    },
    {
      id: "faq-11",
      question: "What restaurants do you recommend for dinner dates?",
      answer: `I love a great meal and wonderful company. Here are my favorite spots:

**Sydney**
Sails on Lavender Bay • Bistecca • Aria
Cafe Sydney • Sixpenny • Quay

**Canberra**
Pilot • Ottoman Cuisine • Monster Kitchen & Bar

**Melbourne**
Attica • Cumulus Inc • Supernormal • Embla

**Brisbane**
Donna Chang • e'cco bistro • Agnes • Aria Brisbane

Can't decide? Tell me your vibe and I'll pick the perfect spot for us.`
    },
    {
      id: "faq-12",
      question: "What if I need to cancel or reschedule?",
      answer: "I understand plans can change. **Reschedules require 48 hours notice.** Please provide at least 48 hours notice for any cancellations or changes. Deposits are non-refundable, however I'm happy to accommodate one reschedule with 48 hours notice. This policy helps me manage my schedule while ensuring we both have the flexibility we need."
    },
    {
      id: "faq-13",
      question: "Are duo bookings available?",
      answer: "Yes! I offer duo experiences with my stunning friend Dakota. Visit the Honey + Dakota page for more information about our doubles experiences."
    }
  ];

  return (
    <div>
      {/* Page Header */}
      <section className="section-padding" style={{ paddingBottom: '40px' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '16px' }}>
              Frequently Asked Questions
            </h1>
            <p className="body-large" style={{ lineHeight: '1.8', color: 'var(--text-secondary)' }}>
              Everything you need to know before we meet.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section style={{ padding: '0 40px 120px' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {faqs.map((faq, index) => (
              <div 
                key={faq.id}
                style={{
                  borderBottom: '1px solid var(--border-light)',
                  marginBottom: '0'
                }}
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  style={{
                    width: '100%',
                    padding: '28px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '24px',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <h3 
                    className="heading-3" 
                    style={{ 
                      margin: 0,
                      color: openIndex === index ? 'var(--burgundy)' : 'var(--text-primary)',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {faq.question}
                  </h3>
                  <span 
                    style={{
                      fontSize: '24px',
                      color: 'var(--burgundy)',
                      fontWeight: 300,
                      flexShrink: 0,
                      transition: 'transform 0.3s ease',
                      transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)'
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  style={{
                    maxHeight: openIndex === index ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease, padding 0.4s ease',
                    paddingBottom: openIndex === index ? '28px' : '0'
                  }}
                >
                  <div 
                    className="body-regular" 
                    style={{ 
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8',
                      margin: 0,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    <SafeText text={faq.answer} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-small" style={{ textAlign: 'center', background: 'var(--ivory)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            Still have questions?
          </h2>
          <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
            I'm here to help. Reach out and let's chat.
          </p>
          <a href="/bookings" className="btn-primary">
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;